import { Box, Flex, Radio, RadioGroup, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { FiAward } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { AwardMethodDto, type EvaluationDto, type EventValueDto } from "../../../../autogen/bff-api";

const formatPrice = (v?: EventValueDto | null): string =>
  v?.value != null ? `${v.value.toLocaleString("nb-NO")} ${v.currency}` : "–";

const formatScore = (points?: number | null): string => (points != null ? points.toFixed(1) : "–");

type AwardBidsTableProps = {
  evaluation: EvaluationDto;
  selectedBidId?: string;
} & ({ mode: "select"; onSelect: (bidId: string) => void } | { mode: "confirmed"; onSelect?: never });

export const AwardBidsTable = ({ evaluation, mode, selectedBidId, onSelect }: AwardBidsTableProps) => {
  const { t } = useTranslation();
  const isLowestPrice = evaluation.awardMethod === AwardMethodDto.LowestPrice;
  const bids = [...evaluation.bids].sort((a, b) => a.rank - b.rank);

  const table = (
    <Table variant="simple">
      <Thead>
        <Tr>
          <Th>{mode === "select" ? t("Select") : t("Winner")}</Th>
          <Th textAlign="center">#</Th>
          <Th>{t("Supplier")}</Th>
          {isLowestPrice ? (
            <Th isNumeric>{t("Bid sum")}</Th>
          ) : (
            <>
              <Th isNumeric>{t("Total score")}</Th>
              {evaluation.criteria.map((c) => (
                <Th key={c.id} isNumeric>
                  {c.name || t(c.type)}
                  {c.weightPercent != null ? ` ${c.weightPercent}%` : ""}
                </Th>
              ))}
            </>
          )}
        </Tr>
      </Thead>
      <Tbody>
        {bids.map((bid) => {
          const isWinner = bid.bidId === selectedBidId;
          return (
            <Tr key={bid.bidId} bg={isWinner ? "green.50" : undefined}>
              <Td
                borderLeft={isWinner ? "3px solid" : undefined}
                borderLeftColor={isWinner ? "green.400" : undefined}
              >
                {mode === "select" ? (
                  <Radio value={bid.bidId} colorScheme="teal" />
                ) : isWinner ? (
                  <Box as={FiAward} color="green.500" boxSize="5" data-testid="award-winner-icon" aria-label={t("Winner") ?? undefined} />
                ) : null}
              </Td>
              <Td textAlign="center">
                <Flex
                  align="center"
                  justify="center"
                  display="inline-flex"
                  boxSize="7"
                  rounded="full"
                  bg={isWinner ? "smPrimary" : "smBackgroundSecondary"}
                  color={isWinner ? "white" : "smMuted"}
                  fontWeight="bold"
                  fontSize="sm"
                >
                  {bid.rank}
                </Flex>
              </Td>
              <Td>
                <Text fontWeight="semibold" color="smText">
                  {bid.supplierName || t("Unknown")}
                </Text>
                {!isLowestPrice && (
                  <Text fontSize="sm" color="smMuted">
                    {formatPrice(bid.effectivePrice ?? bid.price)}
                  </Text>
                )}
              </Td>
              {isLowestPrice ? (
                <Td isNumeric fontWeight="medium" color="smText">
                  {formatPrice(bid.effectivePrice ?? bid.price)}
                </Td>
              ) : (
                <>
                  <Td isNumeric fontWeight="bold" fontFamily="mono" color={isWinner ? "smPrimary" : "smText"}>
                    {bid.weightedTotal.toFixed(1)}
                  </Td>
                  {evaluation.criteria.map((c) => {
                    const cell = bid.cells.find((x) => x.criterionId === c.id);
                    return (
                      <Td key={c.id} isNumeric fontFamily="mono" color="smText">
                        {formatScore(cell?.points)}
                      </Td>
                    );
                  })}
                </>
              )}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
      <TableContainer>
        {mode === "select" && onSelect ? (
          <RadioGroup value={selectedBidId ?? ""} onChange={onSelect}>
            {table}
          </RadioGroup>
        ) : (
          table
        )}
      </TableContainer>
    </Box>
  );
};
